// Главный хук диалога. Связывает голосовую сессию (мост к агенту), распознавание
// речи (Siri/браузер) и озвучку ответа (Siri/браузер). Голос целиком на устройстве,
// серверу уходит только текст.
//
// Поочерёдность реплик (как в голосовом режиме ChatGPT):
//   слушаю → распознал фразу → агент думает → озвучиваю ответ → снова слушаю.
// Пока агент думает/говорит — микрофон не слушает (чтобы не ловить свой же голос).
//
// Режим 'ws' (стрим аудио на бэкенд) сохранён для совместимости и использует
// AudioCapture; режимы 'bridge'/'mock' используют распознавание в браузере.

import { useCallback, useEffect, useRef, useState } from 'react'
import { config } from '../config'
import { AudioCapture, MicError } from '../audio/AudioCapture'
import { AudioPlayer } from '../audio/AudioPlayer'
import { SpeechSynthesizer } from '../audio/SpeechSynthesizer'
import { SpeechRecognizer } from '../audio/SpeechRecognizer'
import { NativeSpeechRecognizer } from '../audio/NativeSpeechRecognizer'
import { isNativeApp } from '../net/platform'
import { WhisperRecorder } from '../audio/WhisperRecorder'
import { ThinkingSound } from '../audio/ThinkingSound'
import { createVoiceSession } from '../net/createVoiceSession'
import { getAmbient, getSpeed, getStt } from '../net/settings'
import { transcribe } from '../net/transcribe'
import { fetchHistory } from '../net/history'
import { resolveAgentById } from '../net/mode'
import type {
  AgentIdentity,
  AgentStatus,
  ConnectionState,
  DialogTurn,
  SessionEvent,
  VoiceSession,
} from '../types'

let turnSeq = 0
const newId = () => `t${Date.now()}_${turnSeq++}`

export interface ConversationState {
  connection: ConnectionState
  callActive: boolean
  micLevel: number
  agentLevel: number
  liveUserText: string
  liveAgentText: string
  history: DialogTurn[]
  agentStatus: AgentStatus
  voiceOutput: boolean
  ttsSupported: boolean
  /** Агент сейчас занят (думает ИЛИ зачитывает) — для кнопки «Стоп». */
  busy: boolean
  /** Агент ДУМАЕТ (ждём текст ответа) — для индикатора «думает…». Гаснет, как только пришёл текст. */
  thinking: boolean
  /** Кто сейчас отвечает (имя + иконка) — для индикатора ожидания. */
  currentAgent: AgentIdentity | null
  /** Состояние записи Whisper: idle | recording | transcribing. */
  recState: 'idle' | 'recording' | 'transcribing'
  error: string | null
}

const INITIAL_STATUS: AgentStatus = { state: 'unknown' }

export function useConversation() {
  const [connection, setConnection] = useState<ConnectionState>('idle')
  const [callActive, setCallActive] = useState(false)
  const [micLevel, setMicLevel] = useState(0)
  const [agentLevel, setAgentLevel] = useState(0)
  const [liveUserText, setLiveUserText] = useState('')
  const [liveAgentText, setLiveAgentText] = useState('')
  const [history, setHistory] = useState<DialogTurn[]>([])
  const [agentStatus, setAgentStatus] = useState<AgentStatus>(INITIAL_STATUS)
  const [voiceOutput, setVoiceOutput] = useState(config.tts.mode !== 'off')
  const [busy, setBusy] = useState(false)
  const [thinking, setThinking] = useState(false)
  const [currentAgent, setCurrentAgent] = useState<AgentIdentity | null>(null)
  // Состояние записи Whisper: idle | recording | transcribing.
  const [recState, setRecState] = useState<'idle' | 'recording' | 'transcribing'>('idle')
  const recStateRef = useRef<'idle' | 'recording' | 'transcribing'>('idle')
  const setRec = useCallback((s: 'idle' | 'recording' | 'transcribing') => {
    recStateRef.current = s
    setRecState(s)
  }, [])
  const [error, setError] = useState<string | null>(null)

  const sessionRef = useRef<VoiceSession | null>(null)
  const captureRef = useRef<AudioCapture | null>(null)
  const playerRef = useRef<AudioPlayer | null>(null)
  const ttsRef = useRef<SpeechSynthesizer | null>(null)
  const recognizerRef = useRef<SpeechRecognizer | NativeSpeechRecognizer | null>(null)
  const whisperRecRef = useRef<WhisperRecorder | null>(null)
  const transcribingRef = useRef(false)
  const sttModeRef = useRef<'browser' | 'whisper'>('browser')
  const thinkingRef = useRef<ThinkingSound | null>(null)
  const pulseTimerRef = useRef<number | null>(null)

  // Состояние текущей реплики агента / поочерёдности.
  const agentTextRef = useRef('')
  const agentIdentityRef = useRef<AgentIdentity | null>(null)
  const committedRef = useRef(false)
  const gotAudioRef = useRef(false)
  const callActiveRef = useRef(false)
  const agentBusyRef = useRef(false) // агент думает или говорит → не слушаем
  const voiceOutputRef = useRef(voiceOutput)
  voiceOutputRef.current = voiceOutput

  const pushTurn = useCallback((speaker: DialogTurn['speaker'], text: string) => {
    if (!text.trim()) return
    setHistory((h) => [...h, { id: newId(), speaker, text: text.trim(), final: true, ts: Date.now() }])
  }, [])

  const commitAgentTurn = useCallback(() => {
    if (committedRef.current) return
    const text = agentTextRef.current
    if (!text.trim()) return
    const who = agentIdentityRef.current
    setHistory((h) => [
      ...h,
      {
        id: newId(),
        speaker: 'agent',
        text: text.trim(),
        final: true,
        ts: Date.now(),
        agentId: who?.id,
        agentName: who?.name,
        agentEmoji: who?.emoji,
      },
    ])
    setLiveAgentText('')
    committedRef.current = true
  }, [])

  // Загрузить историю чата с сервера (синхрон между устройствами).
  const reloadHistory = useCallback(async () => {
    const turns = await fetchHistory()
    if (!turns.length) return
    setHistory(
      turns.map((t) => {
        const ts = t.ts || Date.now()
        if (t.speaker === 'agent') {
          const a = resolveAgentById(t.agentId || 'main')
          return {
            id: newId(),
            speaker: 'agent' as const,
            text: t.text,
            final: true,
            ts,
            agentId: a.id,
            agentName: a.name,
            agentEmoji: a.emoji,
          }
        }
        return { id: newId(), speaker: 'user' as const, text: t.text, final: true, ts }
      }),
    )
  }, [])

  // Возобновить прослушивание (если звонок активен и агент не занят).
  const startListening = useCallback(() => {
    if (!callActiveRef.current || agentBusyRef.current || transcribingRef.current) return
    if (sttModeRef.current === 'whisper') void whisperRecRef.current?.start()
    else recognizerRef.current?.start()
  }, [])

  // Агент закончил реплику → снова слушаем.
  const finishAgentTurn = useCallback(() => {
    agentBusyRef.current = false
    setBusy(false)
    setThinking(false)
    setCurrentAgent(null)
    if (callActiveRef.current) window.setTimeout(() => startListening(), 200)
  }, [startListening])

  // Озвучить ответ и затем продолжить слушать.
  const speakAgentThenListen = useCallback(() => {
    // Озвучка выключена — глушим аудио и сразу слушаем дальше.
    if (!voiceOutputRef.current) {
      playerRef.current?.flush()
      finishAgentTurn()
      return
    }
    // Сервер прислал готовое аудио (нейро-TTS) — играем его, слушаем когда доиграет.
    if (gotAudioRef.current) {
      playerRef.current?.whenIdle(() => finishAgentTurn())
      return
    }
    // Иначе — запасной вариант: синтез браузера (Siri), затем слушаем.
    const m = config.tts.mode
    if (m !== 'off' && m !== 'backend' && ttsRef.current) {
      ttsRef.current.speak(agentTextRef.current, () => finishAgentTurn(), getSpeed())
    } else {
      finishAgentTurn()
    }
  }, [finishAgentTurn])

  const handleEvent = useCallback(
    (e: SessionEvent) => {
      switch (e.type) {
        case 'connection':
          setConnection(e.state)
          break
        case 'user_transcript':
          if (e.final) {
            pushTurn('user', e.text)
            setLiveUserText('')
          } else {
            setLiveUserText(e.text)
          }
          break
        case 'agent_text':
          agentTextRef.current = e.text
          if (e.agent) agentIdentityRef.current = e.agent
          setLiveAgentText(e.text)
          setThinking(false) // пришёл текст — гасим индикатор «думает»
          thinkingRef.current?.stop()
          if (e.final) commitAgentTurn()
          break
        case 'agent_message': {
          // Готовая цельная реплика (в т.ч. одна из нескольких в «Конференции»).
          setThinking(false)
          thinkingRef.current?.stop()
          agentIdentityRef.current = e.agent
          setCurrentAgent(e.agent)
          const who = e.agent
          setHistory((h) => [
            ...h,
            {
              id: newId(),
              speaker: 'agent',
              text: e.text.trim(),
              final: true,
              ts: Date.now(),
              agentId: who.id,
              agentName: who.name,
              agentEmoji: who.emoji,
            },
          ])
          // Копим текст для запасной озвучки (Siri), если серверного аудио нет.
          agentTextRef.current = (agentTextRef.current ? agentTextRef.current + '\n' : '') + e.text
          committedRef.current = true // speech_end не должен дублировать
          if (e.audio) {
            gotAudioRef.current = true
            playerRef.current?.enqueue(e.audio)
          }
          break
        }
        case 'agent_thinking':
          // Между репликами «Конференции» — снова показать индикатор «думает».
          agentBusyRef.current = true
          setBusy(true)
          setThinking(true)
          if (e.agent) {
            agentIdentityRef.current = e.agent
            setCurrentAgent(e.agent)
          }
          break
        case 'agent_speech_start':
          agentBusyRef.current = true
          setBusy(true)
          setThinking(true)
          agentTextRef.current = ''
          agentIdentityRef.current = e.agent ?? null
          setCurrentAgent(e.agent ?? null)
          committedRef.current = false
          gotAudioRef.current = false
          recognizerRef.current?.stop()
          whisperRecRef.current?.abort()
          playerRef.current?.flush()
          ttsRef.current?.cancel()
          thinkingRef.current?.setVariant(getAmbient()) // применить выбор из настроек
          thinkingRef.current?.start() // агент начал думать — фоновый эмбиент
          break
        case 'agent_audio':
          gotAudioRef.current = true
          playerRef.current?.enqueue(e.chunk)
          break
        case 'agent_speech_end':
          thinkingRef.current?.stop()
          commitAgentTurn()
          speakAgentThenListen()
          break
        case 'interrupted':
          setThinking(false)
          thinkingRef.current?.stop()
          playerRef.current?.flush()
          ttsRef.current?.cancel()
          setLiveAgentText('')
          finishAgentTurn()
          break
        case 'agent_status':
          setAgentStatus(e.status)
          break
        case 'error':
          thinkingRef.current?.stop()
          setError(e.message)
          break
      }
    },
    [pushTurn, commitAgentTurn, speakAgentThenListen, finishAgentTurn],
  )

  // Создаём сессию, плеер и синтезатор один раз.
  useEffect(() => {
    const session = createVoiceSession()
    sessionRef.current = session
    const off = session.on(handleEvent)

    playerRef.current = new AudioPlayer({
      sampleRate: config.audio.sampleRate,
      format: config.audio.recvFormat,
      onLevel: setAgentLevel,
    })
    ttsRef.current = new SpeechSynthesizer({
      lang: config.tts.lang,
      rate: config.tts.rate,
      pitch: config.tts.pitch,
      onLevel: setAgentLevel,
    })
    thinkingRef.current = new ThinkingSound(getAmbient())

    // В режимах bridge/mock подключение лёгкое (health-check) — поднимаем сразу,
    // чтобы индикатор показал «на связи». Для ws ждём старта звонка.
    if (config.backend !== 'ws') session.connect()

    return () => {
      off()
      session.disconnect()
      captureRef.current?.stop()
      recognizerRef.current?.abort()
      whisperRecRef.current?.dispose()
      playerRef.current?.close()
      ttsRef.current?.close()
      thinkingRef.current?.close()
      if (pulseTimerRef.current != null) clearInterval(pulseTimerRef.current)
      sessionRef.current = null
      captureRef.current = null
      recognizerRef.current = null
      playerRef.current = null
      ttsRef.current = null
      thinkingRef.current = null
    }
  }, [handleEvent])

  const ensureConnected = useCallback(() => {
    const s = sessionRef.current
    if (!s) return
    if (s.state === 'idle' || s.state === 'closed' || s.state === 'error') s.connect()
  }, [])

  const stopAgentVoice = useCallback(() => {
    playerRef.current?.flush()
    ttsRef.current?.cancel()
  }, [])

  // Кнопка «Стоп»: прервать ответ/обдумывание агента.
  const stop = useCallback(() => {
    sessionRef.current?.interrupt() // эмитит 'interrupted' → глушит и сбрасывает busy
    thinkingRef.current?.stop()
    stopAgentVoice()
    agentBusyRef.current = false
    setBusy(false)
    setThinking(false)
    setCurrentAgent(null)
  }, [stopAgentVoice])

  // ── Запуск/остановка звонка ────────────────────────────────────────────────

  const endCall = useCallback(() => {
    callActiveRef.current = false
    agentBusyRef.current = false
    captureRef.current?.stop()
    captureRef.current = null
    recognizerRef.current?.abort()
    recognizerRef.current = null
    whisperRecRef.current?.dispose()
    whisperRecRef.current = null
    transcribingRef.current = false
    if (pulseTimerRef.current != null) {
      clearInterval(pulseTimerRef.current)
      pulseTimerRef.current = null
    }
    setMicLevel(0)
    setLiveUserText('')
    setCallActive(false)
  }, [])

  const startCallStreaming = useCallback(async () => {
    // Режим стрима аудио на бэкенд (config.backend === 'ws').
    const capture = new AudioCapture({
      targetSampleRate: config.audio.sampleRate,
      frameSize: config.audio.frameSize,
      onLevel: (lvl) => {
        setMicLevel(lvl)
        if (agentBusyRef.current && lvl > 0.12) {
          stopAgentVoice()
          sessionRef.current?.interrupt()
        }
      },
      onFrame: (pcm) => sessionRef.current?.sendAudio(pcm),
    })
    await capture.start()
    captureRef.current = capture
  }, [stopAgentVoice])

  const startCallRecognition = useCallback(() => {
    sttModeRef.current = getStt()

    // Вариант 2: серверный Whisper — запись с авто-стопом по тишине.
    if (sttModeRef.current === 'whisper') {
      const wr = new WhisperRecorder({
        onLevel: (lvl) => setMicLevel(lvl),
        onResult: async (blob, fmt) => {
          transcribingRef.current = true
          setLiveUserText('…')
          let text = ''
          try {
            text = await transcribe(blob, fmt)
          } catch {
            setError('Не удалось распознать речь (Whisper).')
          }
          transcribingRef.current = false
          setLiveUserText('')
          if (text) sessionRef.current?.sendText(text)
          else if (callActiveRef.current && !agentBusyRef.current) startListening()
        },
        onEnd: () => {
          if (callActiveRef.current && !agentBusyRef.current && !transcribingRef.current) {
            window.setTimeout(() => startListening(), 250)
          }
        },
        onError: (msg) => {
          setError('Доступ к микрофону: ' + msg)
          endCall()
        },
      })
      if (!wr.supported) {
        setError('Запись аудио не поддерживается в этом браузере.')
        return false
      }
      whisperRecRef.current = wr
      startListening()
      return true
    }

    // Вариант 1: распознавание на устройстве — браузерное (Siri в Safari) либо
    // нативное Android (в собранном APK, где webkitSpeechRecognition нет).
    const recOpts = {
      lang: config.stt.lang,
      onInterim: (text: string) => setLiveUserText(text),
      onFinal: (text: string) => {
        setLiveUserText('')
        recognizerRef.current?.stop()
        sessionRef.current?.sendText(text)
      },
      onError: (err: string) => {
        if (err === 'not-allowed' || err === 'service-not-allowed') {
          setError('Доступ к микрофону запрещён. Разреши его в настройках.')
          endCall()
        }
        // 'no-speech' / 'aborted' — норм, onEnd перезапустит прослушивание.
      },
      onEnd: () => {
        if (callActiveRef.current && !agentBusyRef.current) {
          window.setTimeout(() => startListening(), 250)
        }
      },
    }
    const rec = isNativeApp() ? new NativeSpeechRecognizer(recOpts) : new SpeechRecognizer(recOpts)
    if (!rec.supported) {
      setError('Распознавание речи не поддерживается в этом браузере. Используй текстовый ввод.')
      return false
    }
    recognizerRef.current = rec

    // Лёгкая «пульсация» индикатора, пока слушаем.
    if (pulseTimerRef.current != null) clearInterval(pulseTimerRef.current)
    pulseTimerRef.current = window.setInterval(() => {
      setMicLevel(agentBusyRef.current ? 0 : 0.3 + Math.random() * 0.4)
    }, 160)

    startListening()
    return true
  }, [startListening, endCall])

  const startCall = useCallback(async () => {
    setError(null)
    ensureConnected()
    await playerRef.current?.unlock()
    ttsRef.current?.unlock()
    void thinkingRef.current?.unlock()

    callActiveRef.current = true
    try {
      if (config.backend === 'ws') {
        await startCallStreaming()
      } else if (config.stt.mode === 'browser') {
        const ok = startCallRecognition()
        if (!ok) {
          callActiveRef.current = false
          return
        }
      } else {
        callActiveRef.current = false
        setError('Голосовой ввод отключён в настройках (VITE_STT_MODE).')
        return
      }
      setCallActive(true)
    } catch (err) {
      callActiveRef.current = false
      const msg = err instanceof MicError ? err.message : 'Не удалось запустить микрофон'
      setError(msg)
      setCallActive(false)
    }
  }, [ensureConnected, startCallStreaming, startCallRecognition])

  // Whisper: один тап — начать запись, повторный тап — остановить и отправить.
  const whisperTurn = useCallback(async () => {
    if (recStateRef.current === 'recording') {
      whisperRecRef.current?.stop() // → onResult → распознавание → отправка
      return
    }
    if (recStateRef.current !== 'idle' || agentBusyRef.current) return
    setError(null)
    ensureConnected()
    await playerRef.current?.unlock()
    ttsRef.current?.unlock()

    const wr = new WhisperRecorder({
      silenceMs: 600000, // без авто-стопа по тишине — управляем нажатием
      maxMs: 60000,
      onLevel: (lvl) => setMicLevel(lvl),
      onResult: async (blob, fmt) => {
        setRec('transcribing')
        setMicLevel(0)
        let text = ''
        try {
          text = await transcribe(blob, fmt)
        } catch {
          setError('Не удалось распознать речь.')
        }
        setRec('idle')
        if (text) sessionRef.current?.sendText(text)
      },
      onEnd: () => {
        whisperRecRef.current?.dispose()
        whisperRecRef.current = null
      },
      onError: (msg) => {
        setError('Доступ к микрофону: ' + msg)
        setRec('idle')
      },
    })
    if (!wr.supported) {
      setError('Запись аудио не поддерживается в этом браузере.')
      return
    }
    whisperRecRef.current?.dispose()
    whisperRecRef.current = wr
    setRec('recording')
    await wr.start()
  }, [ensureConnected, setRec])

  const toggleCall = useCallback(() => {
    // Whisper — пошаговая запись (тап старт / тап стоп), а не непрерывный звонок.
    if (config.backend !== 'ws' && getStt() === 'whisper') {
      void whisperTurn()
      return
    }
    if (callActiveRef.current) endCall()
    else void startCall()
  }, [endCall, startCall, whisperTurn])

  // Текстовый ввод как альтернатива голосу.
  const sendText = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      setError(null)
      ensureConnected()
      void playerRef.current?.unlock()
      ttsRef.current?.unlock()
      void thinkingRef.current?.unlock()
      sessionRef.current?.sendText(trimmed)
    },
    [ensureConnected],
  )

  const toggleVoiceOutput = useCallback(() => {
    setVoiceOutput((on) => {
      const next = !on
      if (!next) stopAgentVoice()
      return next
    })
  }, [stopAgentVoice])

  const dismissError = useCallback(() => setError(null), [])

  const state: ConversationState = {
    connection,
    callActive,
    micLevel,
    agentLevel,
    liveUserText,
    liveAgentText,
    history,
    agentStatus,
    voiceOutput,
    // Озвучка доступна, если сервер шлёт готовое аудио (режимы auto/backend —
    // его играет AudioPlayer) ИЛИ работает браузерный синтез (Siri). На Android
    // браузерный синтез недоступен, но серверное аудио есть — поэтому кнопка
    // «звук вкл/выкл» должна быть активна.
    ttsSupported:
      config.tts.mode === 'auto' ||
      config.tts.mode === 'backend' ||
      (ttsRef.current?.isSupported ?? false),
    busy,
    thinking,
    currentAgent,
    recState,
    error,
  }

  return {
    state,
    startCall,
    endCall,
    toggleCall,
    sendText,
    stop,
    setAgentStatus,
    toggleVoiceOutput,
    dismissError,
    reloadHistory,
  }
}
